import { Avatar, Combobox, Group, InputBase, Loader, Text, useCombobox } from '@mantine/core';
import { notifications } from '@mantine/notifications';
import { AdminPortalPartnerRO } from '@routellers/types/dist/partners-portal/admin/admin-portal-partner.ro';
import { PortalPartnerTypeEnum } from '@routellers/types/dist/partners-portal/enum/portal-partner-type.enum';
import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { api, apiErrorMessage } from '../api';

type Props = {
    label?: string;
    description?: string;
    value: number | null;
    /** When value is unset, match an author by this display name (e.g. legacy route attribution). */
    fallbackName?: string | null;
    onChange: (author: AdminPortalPartnerRO | null) => void;
    required?: boolean;
    disabled?: boolean;
    clearable?: boolean;
};

export const AuthorSelect = ({
    label,
    description,
    value,
    fallbackName,
    onChange,
    required,
    disabled,
    clearable = true,
}: Props) => {
    const { t } = useTranslation();
    const combobox = useCombobox({
        onDropdownClose: () => combobox.resetSelectedOption(),
    });
    const [authors, setAuthors] = useState<AdminPortalPartnerRO[]>([]);
    const [loading, setLoading] = useState(false);
    const [search, setSearch] = useState('');

    useEffect(() => {
        let cancelled = false;

        const load = async () => {
            setLoading(true);

            try {
                const { data } = await api.partnersPortalAdminApi.partners({
                    type: PortalPartnerTypeEnum.AUTHOR,
                });

                if (!cancelled) setAuthors(data || []);
            } catch (error) {
                if (!cancelled) {
                    notifications.show({ color: 'red', message: apiErrorMessage(error) });
                    setAuthors([]);
                }
            } finally {
                if (!cancelled) setLoading(false);
            }
        };

        load();

        return () => {
            cancelled = true;
        };
    }, []);

    const selected = useMemo(() => {
        if (value != null) {
            return authors.find((a) => a.id === value) || null;
        }

        const name = fallbackName?.trim();

        if (!name) return null;

        return authors.find((a) => a.name === name) || null;
    }, [authors, value, fallbackName]);

    const filtered = useMemo(() => {
        const q = search.trim().toLowerCase();

        if (!q) return authors;

        return authors.filter(
            (a) => a.name.toLowerCase().includes(q) || (a.email || '').toLowerCase().includes(q),
        );
    }, [authors, search]);

    const display = selected ? selected.name : search;

    return (
        <Combobox
            store={combobox}
            withinPortal
            onOptionSubmit={(optionValue) => {
                if (optionValue === '__clear__') {
                    onChange(null);
                    setSearch('');
                    combobox.closeDropdown();

                    return;
                }

                const next = authors.find((a) => String(a.id) === optionValue) || null;

                onChange(next);
                setSearch('');
                combobox.closeDropdown();
            }}
        >
            <Combobox.Target>
                <InputBase
                    label={
                        label === undefined ? t('editors.authorSelect.label') : label || undefined
                    }
                    description={description || undefined}
                    required={required}
                    disabled={disabled}
                    pointer
                    rightSection={loading ? <Loader size={14} /> : <Combobox.Chevron />}
                    rightSectionPointerEvents='none'
                    value={combobox.dropdownOpened ? search : display}
                    onChange={(e) => {
                        setSearch(e.currentTarget.value);
                        combobox.openDropdown();
                        combobox.updateSelectedOptionIndex();
                    }}
                    onClick={() => combobox.openDropdown()}
                    onFocus={() => {
                        setSearch('');
                        combobox.openDropdown();
                    }}
                    onBlur={() => {
                        combobox.closeDropdown();
                        setSearch('');
                    }}
                    placeholder={selected ? selected.name : t('editors.authorSelect.placeholder')}
                />
            </Combobox.Target>

            <Combobox.Dropdown>
                <Combobox.Options>
                    {clearable && selected && (
                        <Combobox.Option value='__clear__'>
                            <Text size='sm' c='dimmed'>
                                {t('editors.authorSelect.noAuthor')}
                            </Text>
                        </Combobox.Option>
                    )}
                    {filtered.map((author) => (
                        <Combobox.Option key={author.id} value={String(author.id)}>
                            <Group gap='sm' wrap='nowrap'>
                                <Avatar src={author.avatarUrl || undefined} radius='xl' size={28}>
                                    {author.name.slice(0, 1).toUpperCase()}
                                </Avatar>
                                <div style={{ minWidth: 0 }}>
                                    <Text size='sm' fw={500} truncate>
                                        {author.name}
                                    </Text>
                                    {author.email && (
                                        <Text size='xs' c='dimmed' truncate>
                                            {author.email}
                                        </Text>
                                    )}
                                </div>
                            </Group>
                        </Combobox.Option>
                    ))}
                    {!filtered.length && (
                        <Combobox.Empty>
                            {loading
                                ? t('editors.authorSelect.loading')
                                : t('editors.authorSelect.noAuthorsFound')}
                        </Combobox.Empty>
                    )}
                </Combobox.Options>
            </Combobox.Dropdown>
        </Combobox>
    );
};
